import React, { useState, useEffect } from 'react';
import { Package, Search, Filter, Download, CheckCircle } from 'lucide-react';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config/env.js';

const DeliveredLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({ total: 0, delivered: 0, confirmed: 0, cancelled: 0 });

  useEffect(() => {
    loadDeliveredLeads();
    loadStats();
  }, [searchQuery, filterStatus]);

  const loadDeliveredLeads = async () => {
    try {
      setLoading(true);
      let url = `${SUPABASE_URL}/rest/v1/master_orders?select=*&order=created_at.desc&limit=100`;
      
      if (filterStatus !== 'all') {
        url += `&booking_status=eq.${filterStatus}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // Ensure data is always an array
      let filtered = Array.isArray(data) ? data : [];
      
      if (searchQuery) {
        filtered = filtered.filter(l => 
          l.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          l.order_id?.includes(searchQuery) ||
          l.vehicle_number?.includes(searchQuery)
        );
      }
      
      setLeads(filtered);
    } catch (error) {
      console.error('Error loading delivered leads:', error);
      setLeads([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/master_orders?select=booking_status`, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // Ensure data is always an array
      const dataArray = Array.isArray(data) ? data : [];
      
      const statsData = {
        total: dataArray.length,
        delivered: dataArray.filter(l => l.booking_status === 'delivered' || l.booking_status === 'DELIVERED').length,
        confirmed: dataArray.filter(l => l.booking_status === 'confirmed').length,
        cancelled: dataArray.filter(l => l.booking_status === 'cancelled').length,
      };
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({ total: 0, delivered: 0, confirmed: 0, cancelled: 0 });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Delivered Leads</h1>
        <p className="text-gray-600 mt-1">Manage and track delivered leads</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Total</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Delivered</p>
          <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Confirmed</p>
          <p className="text-2xl font-bold text-blue-600">{stats.confirmed}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Cancelled</p>
          <p className="text-2xl font-bold text-red-600">{stats.cancelled}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by customer name, order ID, or vehicle..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Status</option>
            <option value="delivered">Delivered</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Leads List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : !Array.isArray(leads) || leads.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No delivered leads found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {Array.isArray(leads) && leads.map((lead) => (
                  <tr key={lead.id || lead.order_id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{lead.order_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{lead.customer_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lead.vehicle_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        lead.booking_status === 'delivered' || lead.booking_status === 'DELIVERED' ? 'bg-green-100 text-green-800' :
                        lead.booking_status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                        lead.booking_status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {lead.booking_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveredLeads;

